import { Archive, Flag, Minus, Plus, RotateCcw } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

import { POSITIONS } from "../../../shared/domain/defense";
import {
  buildDefenseFieldLabels,
  playerName,
} from "../../domain/defensePresentation";
import { buildInningLabel } from "../../domain/gamePresentation";
import type {
  DefenseInningDocument,
  PlayerListItem,
} from "../../shared/types/contracts";
import { cn } from "@/shared/lib/cn";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { ConfirmationDialog } from "@/shared/ui/confirmation-dialog";
import { FieldDiagram } from "@/shared/ui/field-diagram";
import { SectionCard } from "@/shared/ui/section-card";
import { RbiAttributionPrompt } from "./RbiAttributionPrompt";
import { actionKeys } from "../../hooks/actionKeys";
import type { IntentStatusView } from "../../hooks/useIntentStatus";
import { EMPTY_INTENT_STATUS } from "../../hooks/useIntentStatus";

interface GameScreenProps {
  teamName: string;
  opponentName: string;
  isHome: boolean;
  inning: number;
  batting: boolean;
  halfInningTransition: boolean;
  completedHalfInningRuns: number;
  outs: number;
  ourScore: number;
  opponentScore: number;
  ourRunsByInning: number[];
  opponentRunsByInning: number[];
  toast: string | null;
  gameFinal: boolean;
  mercyRuleWinner: "us" | "them" | null;
  finalLabel: string;
  serverBusy: boolean;
  gameId?: string | null;
  actionStatus?: IntentStatusView;
  canScore: boolean;
  isOpenInning: boolean;
  openInning: boolean;
  currentRunCap: number;
  opponentRunCap: number;
  pendingRbi: { batterId: string | null; runs: number } | null;
  offenseContent: ReactNode;
  playLogContent: ReactNode;
  players: PlayerListItem[];
  preferences: Record<string, string[]>;
  defense: DefenseInningDocument[] | null;
  historyLength: number;
  undoCrossesHalfInning: boolean;
  onArchive: () => Promise<boolean>;
  onForfeit: (winner: "us" | "them") => Promise<boolean>;
  onKeepPlaying: () => Promise<boolean>;
  onToggleOpenInning: () => Promise<boolean>;
  onAddOpponentRun: (delta: number) => Promise<boolean>;
  onEndHalfInning: () => Promise<boolean>;
  onApplyRbi: (runs: number) => Promise<boolean>;
  onConfirmHalfInningTransition: () => void;
  onShowDefense: () => void;
  onEditDefense: (inning: number) => void;
  onUndo: () => Promise<boolean>;
}

export function GameScreen({
  teamName,
  opponentName,
  isHome,
  inning,
  batting,
  halfInningTransition,
  completedHalfInningRuns,
  outs,
  ourScore,
  opponentScore,
  ourRunsByInning,
  opponentRunsByInning,
  toast,
  gameFinal,
  mercyRuleWinner,
  finalLabel,
  gameId = null,
  actionStatus = EMPTY_INTENT_STATUS,
  canScore,
  isOpenInning,
  openInning,
  currentRunCap,
  opponentRunCap,
  pendingRbi,
  offenseContent,
  playLogContent,
  players,
  preferences,
  defense,
  historyLength,
  undoCrossesHalfInning,
  onArchive,
  onForfeit,
  onKeepPlaying,
  onToggleOpenInning,
  onAddOpponentRun,
  onEndHalfInning,
  onApplyRbi,
  onConfirmHalfInningTransition,
  onShowDefense,
  onEditDefense,
  onUndo,
}: GameScreenProps) {
  const defenseIndex = Math.min(inning - 1, (defense?.length ?? 1) - 1);
  const defenseInning = defense?.[defenseIndex];
  const currentInningRuns = ourRunsByInning[inning - 1] ?? 0;
  const [forfeitOpen, setForfeitOpen] = useState(false);
  const [undoConfirmationOpen, setUndoConfirmationOpen] = useState(false);
  const [mercyDialogDismissed, setMercyDialogDismissed] = useState(false);
  const mercyDialogOpen =
    gameFinal && (mercyRuleWinner === "us" || mercyRuleWinner === "them");
  const mercyWinnerName = mercyRuleWinner === "us" ? teamName : opponentName;
  const archivePending = gameId
    ? actionStatus.isPending(actionKeys.archive(gameId)) ||
      actionStatus.isPending(actionKeys.finalize(gameId))
    : false;
  const forfeitRecording = gameId
    ? actionStatus.isPending(actionKeys.forfeitRecord(gameId))
    : false;
  const forfeitSaving = gameId
    ? actionStatus.isPending(actionKeys.forfeitSave(gameId))
    : false;
  const forfeitPending = forfeitRecording || forfeitSaving;
  const gameOutcomeUnknown = actionStatus.unknownCount > 0;
  const keepPlayingPending = gameId
    ? actionStatus.isPending(actionKeys.keepPlaying(gameId))
    : false;
  const openInningPending = gameId
    ? actionStatus.isPending(actionKeys.openInning(gameId))
    : false;
  const endHalfPending = gameId
    ? actionStatus.isPending(actionKeys.endHalf(gameId))
    : false;
  const rbiPending = gameId
    ? actionStatus.isPending(actionKeys.rbi(gameId))
    : false;
  const archiveUnknown = gameId
    ? actionStatus.phaseFor(actionKeys.archive(gameId)) === "outcome_unknown" ||
      actionStatus.phaseFor(actionKeys.finalize(gameId)) === "outcome_unknown"
    : false;
  const forfeitUnknown = gameId
    ? actionStatus.phaseFor(actionKeys.forfeitRecord(gameId)) ===
        "outcome_unknown" ||
      actionStatus.phaseFor(actionKeys.forfeitSave(gameId)) ===
        "outcome_unknown"
    : false;
  const undoUnknown = gameId
    ? actionStatus.phaseFor(actionKeys.undo(gameId)) === "outcome_unknown"
    : false;
  const keepPlayingUnknown = gameId
    ? actionStatus.phaseFor(actionKeys.keepPlaying(gameId)) ===
      "outcome_unknown"
    : false;
  const openInningUnknown = gameId
    ? actionStatus.phaseFor(actionKeys.openInning(gameId)) === "outcome_unknown"
    : false;
  const opponentRunUnknown = gameId
    ? actionStatus.phaseFor(actionKeys.opponentRun(gameId)) ===
      "outcome_unknown"
    : false;
  const endHalfUnknown = gameId
    ? actionStatus.phaseFor(actionKeys.endHalf(gameId)) === "outcome_unknown"
    : false;
  const rbiUnknown = gameId
    ? actionStatus.phaseFor(actionKeys.rbi(gameId)) === "outcome_unknown"
    : false;
  const showMercyDialog = mercyDialogOpen && !mercyDialogDismissed;

  useEffect(() => {
    if (!mercyDialogOpen) setMercyDialogDismissed(false);
  }, [mercyDialogOpen]);

  const confirmForfeit = async (winner: "us" | "them") => {
    if (await onForfeit(winner)) setForfeitOpen(false);
  };

  const requestUndo = () => {
    if (undoCrossesHalfInning) {
      setUndoConfirmationOpen(true);
      return;
    }
    void onUndo();
  };

  return (
    <div
      className={cn(
        "mx-auto w-full max-w-2xl space-y-2",
        !batting && "pb-12 md:pb-0",
      )}
    >
      <section
        aria-label="Scoreboard"
        className="sticky top-[calc(var(--shell-header-height)+var(--safe-top))] z-20 overflow-hidden rounded-[var(--radius-lg)] border border-border bg-background/95 shadow-[0_10px_30px_rgb(0_7_18/35%)] backdrop-blur-md"
      >
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-3 py-2">
          <TeamScore name={teamName} score={ourScore} highlight align="left" />
          <div className="min-w-[6.5rem] text-center">
            <div className="font-mono text-xs font-bold tracking-[0.12em]">
              {gameFinal ? "FINAL" : buildInningLabel(batting, isHome, inning)}
            </div>
            {gameFinal ? null : (
              <div
                aria-live="polite"
                className="mt-0.5 font-mono text-xs text-foreground-muted"
              >
                OUTS {"●".repeat(outs)}
                {"○".repeat(Math.max(0, 3 - outs))}
              </div>
            )}
          </div>
          <TeamScore name={opponentName} score={opponentScore} align="right" />
        </div>

        {!showMercyDialog && !batting ? (
          <div className="flex gap-1 overflow-x-auto border-t border-border px-2 py-1.5 font-mono text-[0.65rem]">
            {ourRunsByInning.map((runs, index) => (
              <div
                key={index}
                className={
                  index === inning - 1
                    ? "grid min-w-8 place-items-center rounded border border-warning/70 bg-warning/10"
                    : "grid min-w-8 place-items-center rounded border border-border bg-surface/60 text-foreground-muted"
                }
              >
                <span className="opacity-70">{index + 1}</span>
                <span className="text-warning">{runs}</span>
                <span>{opponentRunsByInning[index] ?? 0}</span>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      {toast ? (
        <div
          role="status"
          className="rounded-[var(--radius-sm)] border border-warning/60 bg-warning px-3 py-2 text-center text-sm font-bold text-warning-foreground shadow-sm"
        >
          {toast}
        </div>
      ) : null}

      {gameFinal && !showMercyDialog ? (
        <section
          className={
            ourScore > opponentScore
              ? "rounded-[var(--radius-lg)] border border-success/70 bg-success/12 p-3 text-center"
              : "rounded-[var(--radius-lg)] border border-danger/70 bg-danger/12 p-3 text-center"
          }
        >
          <h2 className="m-0 font-mono text-lg font-bold tracking-[0.12em]">
            {finalLabel}
          </h2>
          <Button
            type="button"
            size="default"
            pending={archivePending && !archiveUnknown}
            pendingLabel="SAVING…"
            onClick={archiveUnknown ? actionStatus.retryUnknown : onArchive}
            className="mt-3 w-full whitespace-normal"
          >
            <Archive aria-hidden="true" />
            {archiveUnknown ? "RETRY CHECK" : "SAVE TO HISTORY & RESET"}
          </Button>
          <Button
            type="button"
            size="xs"
            variant="ghost"
            disabled={!canScore}
            pending={keepPlayingPending}
            pendingLabel={keepPlayingUnknown ? "CHECKING…" : "UPDATING…"}
            onClick={onKeepPlaying}
            className="mt-1"
          >
            KEEP PLAYING
          </Button>
        </section>
      ) : null}

      {!showMercyDialog ? (
        <div className="flex min-h-8 flex-wrap items-center gap-2">
          <Badge variant={isOpenInning ? "warning" : "neutral"}>
            {isOpenInning ? "OPEN INNING — no cap" : `CAP: ${currentRunCap}`}
          </Badge>
          <Badge variant="neutral">INNING RUNS: {currentInningRuns}</Badge>
          <div className="ml-auto flex items-center gap-1">
            {inning < 9 ? (
              <Button
                type="button"
                size="xs"
                variant={openInning ? "selected" : "ghost"}
                aria-pressed={openInning}
                pending={openInningPending}
                pendingLabel={openInningUnknown ? "CHECKING…" : "SAVING…"}
                onClick={onToggleOpenInning}
              >
                {openInning ? "OPEN ✓" : "DECLARE OPEN"}
              </Button>
            ) : null}
            {!gameFinal ? (
              <Button
                type="button"
                size="xs"
                variant="ghost"
                disabled={!canScore || forfeitPending}
                onClick={() => setForfeitOpen(true)}
              >
                <Flag aria-hidden="true" />
                FORFEIT…
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      <AlertDialog
        open={forfeitOpen}
        onOpenChange={(open) => {
          if (!forfeitPending || forfeitUnknown || open) setForfeitOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Record a forfeit?</AlertDialogTitle>
            <AlertDialogDescription>
              This ends the game now and saves it to history as an 8–0 result
              for the winner. Stats already recorded this game are kept, but
              nobody gets GP credit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
            {forfeitUnknown ? (
              <Button type="button" onClick={actionStatus.retryUnknown}>
                RETRY CHECK
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="default"
                  pending={forfeitPending}
                  pendingLabel={forfeitSaving ? "SAVING…" : "RECORDING…"}
                  onClick={() => void confirmForfeit("us")}
                >
                  THEY FORFEIT — WE WIN 8–0
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  pending={forfeitPending}
                  pendingLabel={forfeitSaving ? "SAVING…" : "RECORDING…"}
                  onClick={() => void confirmForfeit("them")}
                >
                  WE FORFEIT — WE LOSE 0–8
                </Button>
              </>
            )}
            <AlertDialogCancel disabled={forfeitPending && !forfeitUnknown}>
              {forfeitUnknown ? "CLOSE" : "CANCEL"}
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showMercyDialog}
        onOpenChange={(open) => {
          if (!open && archiveUnknown) setMercyDialogDismissed(true);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader className="text-center">
            <AlertDialogTitle>MERCY RULE</AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              {mercyWinnerName} leads{" "}
              {mercyRuleWinner === "us" ? ourScore : opponentScore}–
              {mercyRuleWinner === "us" ? opponentScore : ourScore}. The 15-run
              mercy rule applies. End and save this game, or continue playing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <RbiAttributionPrompt
            pendingRbi={pendingRbi}
            onApplyRbi={onApplyRbi}
            pending={rbiPending}
            unknown={rbiUnknown}
          />
          <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              type="button"
              pending={archivePending && !archiveUnknown}
              pendingLabel="SAVING…"
              disabled={
                pendingRbi !== null || (archivePending && !archiveUnknown)
              }
              onClick={archiveUnknown ? actionStatus.retryUnknown : onArchive}
            >
              {archiveUnknown ? "RETRY CHECK" : "END GAME"}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={!canScore || keepPlayingPending || archiveUnknown}
              pending={keepPlayingPending}
              pendingLabel={keepPlayingUnknown ? "CHECKING…" : "UPDATING…"}
              onClick={async () => {
                if (await onKeepPlaying()) onConfirmHalfInningTransition();
              }}
            >
              CONTINUE PLAYING
            </Button>
            {archiveUnknown ? (
              <AlertDialogCancel>CLOSE</AlertDialogCancel>
            ) : null}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={halfInningTransition && !showMercyDialog}>
        <AlertDialogContent>
          <AlertDialogHeader className="text-center">
            <AlertDialogTitle>INNING COMPLETE</AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              <span className="block font-mono text-4xl font-black text-warning">
                {completedHalfInningRuns}
              </span>
              <span className="font-mono text-xs font-bold tracking-[0.12em] text-foreground">
                {completedHalfInningRuns === 1 ? "RUN" : "RUNS"} SCORED THIS
                INNING
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              type="button"
              className="w-full"
              onClick={onConfirmHalfInningTransition}
            >
              CONFIRM INNING
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConfirmationDialog
        open={undoConfirmationOpen}
        title="Undo across the half-inning boundary?"
        description="This returns the game to the previous half-inning. If the opponent was batting, you can correct their score before continuing."
        confirmLabel="UNDO & RETURN"
        pending={gameOutcomeUnknown}
        pendingLabel="CHECKING…"
        unknown={gameOutcomeUnknown}
        onRetryUnknown={actionStatus.retryUnknown}
        onConfirm={() => {
          setUndoConfirmationOpen(false);
          void onUndo();
        }}
        onOpenChange={setUndoConfirmationOpen}
      />

      {showMercyDialog ? null : (
        <>
          {batting ? (
            offenseContent
          ) : halfInningTransition ? null : (
            <OpponentHalfInning
              teamName={teamName}
              opponentName={opponentName}
              inning={inning}
              isOpenInning={isOpenInning}
              opponentRunCap={opponentRunCap}
              inningRuns={opponentRunsByInning[inning - 1] ?? 0}
              defenseInning={defenseInning}
              defenseIndex={defenseIndex}
              players={players}
              preferences={preferences}
              gameOutcomeUnknown={gameOutcomeUnknown}
              opponentRunUnknown={opponentRunUnknown}
              endHalfPending={endHalfPending}
              endHalfUnknown={endHalfUnknown}
              onAddOpponentRun={onAddOpponentRun}
              onEndHalfInning={onEndHalfInning}
              onShowDefense={onShowDefense}
              onEditDefense={onEditDefense}
            />
          )}

          {playLogContent}

          <div className="flex items-center gap-1.5 rounded-[var(--radius-sm)] border border-border bg-surface/55 p-1.5">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!historyLength || !canScore || gameOutcomeUnknown}
              pending={undoUnknown}
              pendingLabel="CHECKING…"
              onClick={requestUndo}
              className="min-w-0 flex-1"
            >
              <RotateCcw aria-hidden="true" />
              UNDO
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function TeamScore({
  name,
  score,
  highlight = false,
  align,
}: {
  name: string;
  score: number;
  highlight?: boolean;
  align: "left" | "right";
}) {
  return (
    <div className={align === "right" ? "min-w-0 text-right" : "min-w-0"}>
      <div className="truncate text-[0.68rem] text-foreground-muted">
        {name}
      </div>
      <div
        className={
          highlight
            ? "font-mono text-2xl leading-none font-bold text-warning"
            : "font-mono text-2xl leading-none font-bold"
        }
      >
        {score}
      </div>
    </div>
  );
}

function OpponentHalfInning({
  teamName,
  opponentName,
  inning,
  isOpenInning,
  opponentRunCap,
  inningRuns,
  defenseInning,
  defenseIndex,
  players,
  preferences,
  gameOutcomeUnknown,
  opponentRunUnknown,
  endHalfPending,
  endHalfUnknown,
  onAddOpponentRun,
  onEndHalfInning,
  onShowDefense,
  onEditDefense,
}: {
  teamName: string;
  opponentName: string;
  inning: number;
  isOpenInning: boolean;
  opponentRunCap: number;
  inningRuns: number;
  defenseInning: DefenseInningDocument | undefined;
  defenseIndex: number;
  players: PlayerListItem[];
  preferences: Record<string, string[]>;
  gameOutcomeUnknown: boolean;
  opponentRunUnknown: boolean;
  endHalfPending: boolean;
  endHalfUnknown: boolean;
  onAddOpponentRun: (delta: number) => void;
  onEndHalfInning: () => void;
  onShowDefense: () => void;
  onEditDefense: (inning: number) => void;
}) {
  return (
    <div className="space-y-2">
      <SectionCard
        title={`${opponentName} batting — inning ${inning}`}
        description={
          isOpenInning ? "open inning — no cap" : `cap: ${opponentRunCap} runs`
        }
        contentClassName="space-y-3"
      >
        <div className="flex items-center justify-center gap-3">
          <Button
            type="button"
            size="icon"
            variant="outline"
            aria-label="−"
            disabled={gameOutcomeUnknown}
            pending={opponentRunUnknown}
            pendingLabel={<span className="sr-only">Checking score</span>}
            onClick={() => onAddOpponentRun(-1)}
          >
            <Minus aria-hidden="true" />
          </Button>
          <output
            aria-label={`${opponentName} runs this inning`}
            className="min-w-16 text-center font-mono text-4xl leading-none font-bold text-warning"
          >
            {inningRuns}
          </output>
          <Button
            type="button"
            size="icon"
            variant="outline"
            aria-label="+"
            disabled={gameOutcomeUnknown}
            pending={opponentRunUnknown}
            pendingLabel={<span className="sr-only">Checking score</span>}
            onClick={() => onAddOpponentRun(1)}
          >
            <Plus aria-hidden="true" />
          </Button>
        </div>
        <Button
          type="button"
          size="scoring"
          variant="secondary"
          onClick={onEndHalfInning}
          disabled={gameOutcomeUnknown}
          pending={endHalfPending}
          pendingLabel={endHalfUnknown ? "CHECKING…" : "ENDING INNING…"}
          className="w-full whitespace-normal"
        >
          END OF INNING → {teamName} BATS
        </Button>
      </SectionCard>

      {defenseInning ? (
        <DefensePreview
          inning={inning}
          defenseIndex={defenseIndex}
          defenseInning={defenseInning}
          players={players}
          preferences={preferences}
          onEditDefense={onEditDefense}
        />
      ) : (
        <div className="rounded-[var(--radius-sm)] border border-border bg-surface/55 px-3 py-2 text-sm text-foreground-muted">
          No defensive rotation generated — set it up on the{" "}
          <button
            type="button"
            onClick={onShowDefense}
            className="font-bold text-warning underline underline-offset-2"
          >
            DEFENSE
          </button>{" "}
          screen.
        </div>
      )}
    </div>
  );
}

function DefensePreview({
  inning,
  defenseIndex,
  defenseInning,
  players,
  preferences,
  onEditDefense,
}: {
  inning: number;
  defenseIndex: number;
  defenseInning: DefenseInningDocument;
  players: PlayerListItem[];
  preferences: Record<string, string[]>;
  onEditDefense: (inning: number) => void;
}) {
  const nameOf = (id: string) => playerName(players, id);
  const fieldLabels = buildDefenseFieldLabels(
    defenseInning,
    players,
    preferences,
    POSITIONS,
  );

  return (
    <SectionCard
      title={`ON THE FIELD — INNING ${inning}`}
      actions={
        <Button
          type="button"
          size="xs"
          variant="ghost"
          onClick={() => onEditDefense(defenseIndex)}
        >
          edit
        </Button>
      }
      contentClassName="p-0"
      className="overflow-hidden"
    >
      <FieldDiagram
        labels={fieldLabels}
        title={`Defensive positions for inning ${inning}`}
        className="mx-auto block w-full max-w-lg"
      />
      {defenseInning.sitters.length > 0 ? (
        <div className="border-t border-border px-3 py-2 text-sm">
          <span className="font-mono text-danger">SIT: </span>
          {defenseInning.sitters.map(nameOf).join(", ")}
        </div>
      ) : null}
    </SectionCard>
  );
}
